import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Flex,
  useColorModeValue,
  Spinner,
  Button,
  IconButton,
  Progress,
  VStack,
  HStack,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { ArrowBackIcon, RepeatIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../../../../../../services/api';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_VERCEL_API_URL 
  : process.env.REACT_APP_LOCAL_API_URL;

const BuscarDadosJurisConsultRJ = () => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [missingData, setMissingData] = useState([]);
  const [progress, setProgress] = useState(0);
  const [currentSync, setCurrentSync] = useState(null);
  
  const navigate = useNavigate();
  const toast = useToast();
  
  const bg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.700', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const itemBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    checkMissingData();
  }, []);

  const checkMissingData = async () => {
    setChecking(true);
    try {
      const token = getToken();
      
      // First get all selected precatorios
      const selectedResponse = await fetch(`${API_BASE_URL}/precatorioRJ/selecionados`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!selectedResponse.ok) {
        throw new Error('Erro ao buscar precatórios selecionados');
      }

      const selectedData = await selectedResponse.json();
      const selectedIds = selectedData.data.map(p => p._id);

      // Check which ones are missing JurisConsult data
      const missingResponse = await fetch(`${API_BASE_URL}/precatorioRJ/check-missing-jurisconsult`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ precatorioIds: selectedIds })
      });

      if (!missingResponse.ok) {
        throw new Error('Erro ao verificar dados faltantes');
      }

      const missingResult = await missingResponse.json();
      setMissingData(missingResult.missingData || []);
      
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao verificar dados',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setChecking(false);
    }
  };

  const syncJurisConsultData = async () => {
    if (missingData.length === 0) {
      toast({
        title: 'Nada para sincronizar',
        description: 'Todos os precatórios já possuem dados do JurisConsult',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSyncing(true);
    setProgress(0);
    
    const token = getToken();
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < missingData.length; i++) {
      const precatorio = missingData[i];
      setCurrentSync(`Sincronizando ${precatorio.numero_precatorio}...`);
      
      try {
        const response = await fetch(
          `${API_BASE_URL}/precatorioRJ/jurisconsult/${precatorio.id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }

      setProgress(((i + 1) / missingData.length) * 100);
      
      // Add a small delay between requests to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setSyncing(false);
    setCurrentSync(null);
    
    toast({
      title: 'Sincronização concluída',
      description: `${successCount} sucesso, ${errorCount} erros`,
      status: successCount > 0 ? 'success' : 'error',
      duration: 5000,
      isClosable: true,
    });

    // Refresh the missing data
    await checkMissingData();
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Flex align="center">
          <IconButton
            icon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/precatorios/rj/selecionadas')}
            variant="ghost"
            aria-label="Voltar"
            mr={4}
          />
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
              Sincronizar Dados JurisConsult - RJ
            </Text>
            <Text color={textColor} mt={1}>
              Buscar informações detalhadas dos precatórios
            </Text>
          </Box>
        </Flex>
        
        <Button
          leftIcon={<RepeatIcon />}
          onClick={checkMissingData}
          isLoading={checking}
          variant="outline"
        >
          Verificar Novamente
        </Button>
      </Flex>

      <VStack spacing={6} align="stretch">
        {checking ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            <Alert status={missingData.length > 0 ? 'warning' : 'success'}>
              <AlertIcon />
              <AlertDescription>
                {missingData.length > 0
                  ? `${missingData.length} precatórios precisam de sincronização`
                  : 'Todos os precatórios estão sincronizados!'}
              </AlertDescription>
            </Alert>

            {missingData.length > 0 && (
              <Box bg={bg} p={6} borderRadius="lg" borderWidth="1px">
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="bold">
                    Precatórios sem dados JurisConsult:
                  </Text>
                  
                  <Box maxH="300px" overflowY="auto">
                    <VStack spacing={2} align="stretch">
                      {missingData.map((item, index) => (
                        <Box
                          key={item.id}
                          p={2}
                          borderWidth="1px"
                          borderRadius="md"
                          bg={itemBg}
                        >
                          <Text fontSize="sm">
                            {index + 1}. {item.numero_precatorio}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>

                  {syncing && (
                    <VStack spacing={2} align="stretch">
                      <Text fontSize="sm" color={textColor}>
                        {currentSync}
                      </Text>
                      <Progress value={progress} size="sm" colorScheme="blue" />
                      <Text fontSize="xs" color={textColor}>
                        {Math.round(progress)}% concluído
                      </Text>
                    </VStack>
                  )}

                  <Button
                    colorScheme="blue"
                    onClick={syncJurisConsultData}
                    isLoading={syncing}
                    loadingText="Sincronizando..."
                    size="lg"
                    width="full"
                  >
                    Iniciar Sincronização
                  </Button>
                </VStack>
              </Box>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
};

export default BuscarDadosJurisConsultRJ;